/**
 * Memo System - Main Layout Component
 * Email-like interface with left navigation and content area
 */

import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { makeStyles, Button, Text, Badge, Spinner } from '@fluentui/react-components';
import {
  MailInboxRegular,
  SendRegular,
  DraftsRegular,
  ArchiveRegular,
  DeleteRegular,
  ComposeRegular,
  SearchRegular,
} from '@fluentui/react-icons';
import { ApiClient, newToast } from '../../../configurations/index.config';
import { getErrorMessage } from '../../../types';

const useStyles = makeStyles({
  layout: {
    display: 'flex',
    height: 'calc(100vh - 96px)',
    backgroundColor: '#ffffff',
    border: '1px solid #e1dfdd',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  sidebar: {
    width: '240px',
    backgroundColor: '#fafafa',
    borderRight: '1px solid #e1dfdd',
    display: 'flex',
    flexDirection: 'column',
    padding: '16px 0',
  },
  composeButton: {
    margin: '0 16px 16px 16px',
    backgroundColor: '#008272',
    color: '#ffffff',
    fontWeight: '600',
    ':hover': {
      backgroundColor: '#006d5f',
    },
  },
  navSection: {
    flex: 1,
    overflowY: 'auto',
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 16px',
    cursor: 'pointer',
    color: '#323130',
    fontSize: '14px',
    border: 'none',
    backgroundColor: 'transparent',
    width: '100%',
    textAlign: 'left',
    transition: 'background-color 0.1s ease',
    ':hover': {
      backgroundColor: '#f3f2f1',
    },
  },
  navItemContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    flex: 1,
  },
  navCount: {
    fontSize: '12px',
    color: '#605e5c',
  },
  navItemActive: {
    backgroundColor: '#e6f7f5',
    fontWeight: '600',
    borderLeft: '3px solid #008272',
  },
  navIcon: {
    fontSize: '20px',
  },
  navLabel: {
    flex: 1,
  },
  contentArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    overflow: 'hidden',
  },
  header: {
    padding: '16px 24px',
    borderBottom: '1px solid #e1dfdd',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  searchInput: {
    flex: 1,
    maxWidth: '400px',
  },
  mailboxInfo: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px 24px',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
  },
});

interface MemoStats {
  unread_inbox: number;
  total_inbox: number;
  draft_count: number;
  sent_today: number;
  archived_count: number;
  trash_count: number;
}

interface Mailbox {
  id: number;
  friendly_alias: string;
  primary_address: string;
  status: string;
}

export const MemoLayout: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState<MemoStats | null>(null);
  const [mailbox, setMailbox] = useState<Mailbox | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMemoData();
  }, []);

  const loadMemoData = async () => {
    try {
      setLoading(true);

      // Load mailbox info and stats
      const [mailboxRes, statsRes] = await Promise.all([
        ApiClient.get('/memo/my-mailbox'),
        ApiClient.get('/memo/stats'),
      ]);

      if (mailboxRes.data.status === 'success') {
        setMailbox(mailboxRes.data.data);
      }

      if (statsRes.data.status === 'success') {
        setStats(statsRes.data.data);
      }
    } catch (error) {
      newToast('error', getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const navItems = [
    {
      path: '/account/memo/inbox',
      label: 'Inbox',
      icon: <MailInboxRegular className={styles.navIcon} />,
      count: stats?.total_inbox || 0,
      badge: stats?.unread_inbox || 0,
    },
    {
      path: '/account/memo/sent',
      label: 'Sent',
      icon: <SendRegular className={styles.navIcon} />,
      count: stats?.sent_today || 0,
      badge: 0,
    },
    {
      path: '/account/memo/drafts',
      label: 'Drafts',
      icon: <DraftsRegular className={styles.navIcon} />,
      count: stats?.draft_count || 0,
      badge: 0,
    },
    {
      path: '/account/memo/archived',
      label: 'Archived',
      icon: <ArchiveRegular className={styles.navIcon} />,
      count: stats?.archived_count || 0,
      badge: 0,
    },
    {
      path: '/account/memo/trash',
      label: 'Trash',
      icon: <DeleteRegular className={styles.navIcon} />,
      count: stats?.trash_count || 0,
      badge: 0,
    },
  ];

  const isActive = (path: string) => location.pathname === path;

  const handleCompose = () => {
    navigate('/account/memo/compose');
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" label="Loading memo system..." />
      </div>
    );
  }

  return (
    <div className={styles.layout}>
      {/* Left Sidebar */}
      <div className={styles.sidebar}>
        <Button
          appearance="primary"
          icon={<ComposeRegular />}
          className={styles.composeButton}
          onClick={handleCompose}
        >
          Compose
        </Button>

        <div className={styles.navSection}>
          {navItems.map(item => (
            <button
              key={item.path}
              className={`${styles.navItem} ${isActive(item.path) ? styles.navItemActive : ''}`}
              onClick={() => navigate(item.path)}
            >
              {item.icon}
              <div className={styles.navItemContent}>
                <span className={styles.navLabel}>{item.label}</span>
                <span className={styles.navCount}>
                  {item.count} {item.label === 'Drafts' ? 'drafts' : 'messages'}
                </span>
              </div>
              {item.badge > 0 && (
                <Badge appearance="filled" color="important" size="small">
                  {item.badge}
                </Badge>
              )}
            </button>
          ))}
        </div>

        {mailbox && (
          <div style={{ padding: '16px', borderTop: '1px solid #e1dfdd' }}>
            <Text size={200} style={{ color: '#605e5c', display: 'block' }}>
              Your address:
            </Text>
            <Text size={300} weight="semibold" style={{ display: 'block' }}>
              {mailbox.friendly_alias}
            </Text>
          </div>
        )}
      </div>

      {/* Content Area */}
      <div className={styles.contentArea}>
        <div className={styles.content}>
          <Outlet context={{ stats, mailbox, refreshStats: loadMemoData }} />
        </div>
      </div>
    </div>
  );
};

export default MemoLayout;
